import {
  ErrorCodes,
  ExecuteParameters,
  Model,
  ParameterId as ApiParameterId,
  VerbId as ApiVerbId,
} from '@tableau/api-internal-contract-js';
import {
  DocCommands as PresLayerDocCommandId,
  SrvCommands as PresLayerSrvCommandId,
  UICommands as PresLayerUICommandId,
} from '../../gen-src/preslayer/CommandIds';
import { ParameterId as PresLayerParameterId } from '../../gen-src/preslayer/Params';
import { InternalTableauError } from '../InternalTableauError';
import { ApiToPresLayerMappingRegistry, PresLayerToApiMappingRegistry } from './ParameterMappingRegistryFactory';

/**
 * This is an enum used only internally by the api-core project.
 * This enum represents the difference namespaces available for commands.
 */
export enum CommandNamespace {
  TabDoc = 'tabdoc',
  TabSrv = 'tabsrv',
  TabUI = 'tabui',
}

/**
 * Represents an input or output paramter for a command.
 *
 * @export
 * @class CommandParameter
 */
export class CommandParameter {
  private _isOptional = false;
  private _defaultValue?: object;

  /**
   * Creates an instance of CommandParameter.
   * @param {ApiParameterId} apiId
   * @param {PresLayerParameterId} presLayerId
   * @memberof CommandParameter
   */
  public constructor(private _apiId: ApiParameterId, private _presLayerId: PresLayerParameterId) {}

  public static makeOptional(apiId: ApiParameterId, presLayerId: PresLayerParameterId): CommandParameter {
    const newObj = new this(apiId, presLayerId);
    newObj._isOptional = true;
    return newObj;
  }

  // tslint:disable-next-line:no-any
  public static makeWithDefaultValue(apiId: ApiParameterId, presLayerId: PresLayerParameterId, defaultValue: any): CommandParameter {
    const newObj = new this(apiId, presLayerId);
    newObj._defaultValue = defaultValue;
    return newObj;
  }

  /**
   * The Id of the Api Parameter
   *
   * @readonly
   * @type {ApiParameterId}
   * @memberof CommandParameter
   */
  public get apiId(): ApiParameterId {
    return this._apiId;
  }

  /**
   * The Id of the Pres Layer Parameter
   *
   * @readonly
   * @type {PresLayerParameterId}
   * @memberof CommandParameter
   */
  public get presLayerId(): PresLayerParameterId {
    return this._presLayerId;
  }

  /**
   * Whether or not this is an optional parameter
   *
   * @readonly
   * @type {boolean}
   * @memberof CommandParameter
   */
  public get isOptional(): boolean {
    return this._isOptional;
  }

  /**
   * Default value for the parameter
   * @readonly
   * @type {object}
   * @memberof CommandParameter
   */
  public get defaultValue(): object | undefined {
    return this._defaultValue;
  }
}

/**
 * Represents a the complete mapping of an ApiCommand to its corresponding pres-layer command
 * and the conversion of the output value as well
 *
 * @export
 * @class CommandRegistration
 *
 * @param {ApiVerbId} _apiVerbId the internal api verb ID of this command.
 * @param {PresLayerDocCommandId | PresLayerSrvCommandId | PresLayerUICommandId} _commandId
 *   the pres layer command to execute
 * @param {Array<CommandParameter>} _inputParameters list of parameters to send with this command to the platform.
 * @param {CommandParameter} [isOptional] expected return paramters from the platform.
 * @param {CommandNamespace} [isOptional] namespace of the command (defaults to TabDoc if not provided).
 */
export class CommandRegistration {
  public constructor(
    private _apiVerbId: ApiVerbId,
    private _commandId: PresLayerDocCommandId | PresLayerSrvCommandId | PresLayerUICommandId,
    private _inputParameters: Array<CommandParameter>,
    private _outputParameters?: Array<CommandParameter>,
    private _namespace?: CommandNamespace,
  ) {}

  public get apiVerbId(): ApiVerbId {
    return this._apiVerbId;
  }

  public get commandId(): PresLayerDocCommandId | PresLayerSrvCommandId | PresLayerUICommandId {
    return this._commandId;
  }

  public get namespace(): CommandNamespace {
    return this._namespace || CommandNamespace.TabDoc;
  }

  public get inputs(): Array<CommandParameter> {
    return this._inputParameters || new Array<CommandParameter>();
  }

  public get output(): Array<CommandParameter> | undefined {
    return this._outputParameters;
  }

  /**
   * Whether or not this command requires an add-in locator to be injected into it
   *
   * @readonly
   * @type {boolean}
   * @memberof CommandRegistration
   */
  public get needsAddInLocator(): boolean {
    for (const input of this.inputs) {
      if (input.apiId === ApiParameterId.ExtensionLocator) {
        return true;
      }
    }

    return false;
  }

  /**
   * Creates a dictionary of pres-layer parameters from the input dictionary
   * of api parameters.
   *
   * @param {ExecuteParameters} parameters - The parameters to convert
   * @param {ApiToPresLayerMappingRegistry} apiToPresLayer - mapping to determine the conversion
   * @returns {{ [paramId: string]: object; }} - parameters to send to the pres-layer
   * @memberof CommandRegistration
   */
  public createParams(parameters: ExecuteParameters, apiToPresLayer: ApiToPresLayerMappingRegistry): { [paramId: string]: object } {
    const result: { [paramId: string]: object } = {};

    for (const inputParam of this.inputs) {
      // If the parameter is not there, see if it's optional or has a default value
      if (!parameters.hasOwnProperty(inputParam.apiId)) {
        if (inputParam.isOptional) {
          continue;
        } else if (inputParam.defaultValue) {
          result[inputParam.presLayerId] = inputParam.defaultValue;
          continue;
        } else {
          throw new InternalTableauError(ErrorCodes.MISSING_PARAMETER, `Missing input parameter: ${inputParam.apiId}`);
        }
      }

      // Find the conversion function for this parameter
      const conversionFn = apiToPresLayer.get(inputParam.apiId, inputParam.presLayerId);

      // Convert the pres model and insert it into our results object
      result[inputParam.presLayerId] = conversionFn(parameters[inputParam.apiId]);
    }

    return result;
  }

  /**
   * Processes the result received back after executing a pres-layer command
   *
   * @param {{ [docParamId: string]: object; }} resultParameters
   * @param {PresLayerToApiMappingRegistry} presLayerToApi
   * @returns {Model}
   * @memberof CommandRegistration
   */
  public ProcessResult(resultParameters: { [docParamId: string]: object }, presLayerToApi: PresLayerToApiMappingRegistry): Model {
    // TODO: Add error handling
    if (!this.output || !this.output.length) {
      const noOutputModel: Model = {};
      return noOutputModel;
    }
    if (this.output.length > 1) {
      return this.processResultWithMultipleReturnValues(resultParameters, presLayerToApi);
    } else {
      return this.processResultWithSingleReturnValue(resultParameters, presLayerToApi);
    }
  }

  // tslint:disable-next-line:max-line-length
  private processResultWithMultipleReturnValues(
    resultParameters: { [docParamId: string]: object },
    presLayerToApi: PresLayerToApiMappingRegistry,
  ): Model {
    // missingResultParameters variable will hold non optional parameters which are missing from the api response.
    let missingResultParameters: Array<CommandParameter> = [];

    if (resultParameters) {
      missingResultParameters = this.output!.filter((o) => !resultParameters.hasOwnProperty(o.presLayerId) && !o.isOptional);
    }

    if (!resultParameters || missingResultParameters.length) {
      // If we don't have any results at all, then all we can report is what output parameters are missing.
      // but if the command returned some results, it will have an error in it, and we can expose that to caller.
      if (!resultParameters) {
        throw new InternalTableauError(
          ErrorCodes.MISSING_PARAMETER,
          `Missing output parameter(s): ${missingResultParameters.map((param) => param.presLayerId).join(', ')}`,
        );
      }
      throw new InternalTableauError(ErrorCodes.INTERNAL_ERROR, JSON.stringify(resultParameters));
    }

    const result: Model = {};
    this.output!.forEach((o) => {
      if (resultParameters[o.presLayerId]) {
        const conversionFn = presLayerToApi.get(o.presLayerId, o.apiId);
        result[o.apiId] = conversionFn(resultParameters[o.presLayerId]);
      }
    });
    return result;
  }

  // tslint:disable-next-line:max-line-length
  private processResultWithSingleReturnValue(
    resultParameters: { [docParamId: string]: object },
    presLayerToApi: PresLayerToApiMappingRegistry,
  ): Model {
    const output = this.output![0];
    if (!resultParameters || !resultParameters.hasOwnProperty(output.presLayerId)) {
      // if the output result is marked as optional, we don't consider this an error situation and just return an empty model
      // if the output result has a default value, then we return that when the result is empty
      if (output.isOptional) {
        return {};
      } else if (output.defaultValue) {
        return output.defaultValue;
      }

      // If we don't have any results at all, then all we can report is that the output parameter is missing.
      // but if the command returned some results, it will have an error in it, and we can expose that to caller.
      if (!resultParameters) {
        throw new InternalTableauError(ErrorCodes.MISSING_PARAMETER, `Missing output parameter: ${output.presLayerId}`);
      }
      throw new InternalTableauError(ErrorCodes.INTERNAL_ERROR, JSON.stringify(resultParameters));
    }

    const conversionFn = presLayerToApi.get(output.presLayerId, output.apiId);
    return conversionFn(resultParameters[output.presLayerId]);
  }
}
